/** Video via LiveKit (SFU). Each phone publishes one stream to the LiveKit server, which
 *  forwards it to the other seats at a quality each viewer can handle. If LiveKit is not
 *  configured, or cannot connect, the caller falls back to the peer-to-peer mesh in table.ts. */
import { Room, RoomEvent, Track } from 'livekit-client';
import type { RemoteParticipant, RemoteTrack } from 'livekit-client';
import { NET, myStream, socket } from '../state';
import { REL, VID_BY_REL } from './online';

let room: Room | null = null;
let starting = false;
let offMode = false;               // server says LiveKit is not configured
let meshFallback: ((peers: any[]) => void) | null = null;
let unlockBound = false;

/** Table.ts registers how to build peer-to-peer connections when LiveKit is not used. */
export function setMeshFallback(fn: (peers: any[]) => void): void { meshFallback = fn; }

/** True while LiveKit owns the video (or is connecting), so the mesh stays idle. */
export const mediaOn = (): boolean => !!room || starting;

function playRemote(v: HTMLVideoElement): void {
  try { const r: any = v.play(); if (r && r.catch) r.catch(() => { /* waits for a tap */ }); } catch (e) { /* ignore */ }
}

function bindUnlock(): void {
  if (unlockBound) return;
  unlockBound = true;
  const unlock = () => {
    document.querySelectorAll('video').forEach((v) => playRemote(v as HTMLVideoElement));
    try { room?.startAudio().catch(() => { /* ignore */ }); } catch (e) { /* ignore */ }
  };
  ['touchend', 'click', 'keydown'].forEach((ev) => document.addEventListener(ev, unlock, { passive: true }));
}

const seatOf = (p: RemoteParticipant): number | null => {
  const n = Number(String(p.identity).split(':')[1]);
  return Number.isFinite(n) ? n : null;
};

const vidFor = (seat: number): HTMLVideoElement | null =>
  document.getElementById(VID_BY_REL[REL(seat)] || 'vid1') as HTMLVideoElement | null;

function attachRemote(track: RemoteTrack, p: RemoteParticipant): void {
  const seat = seatOf(p);
  const vid = seat == null ? null : vidFor(seat);
  console.log('[media] attach', track.kind, 'seat', seat, 'tile', vid && vid.id);
  if (!vid) return;
  const ms: MediaStream = vid.srcObject instanceof MediaStream ? vid.srcObject : new MediaStream();
  const mt = track.mediaStreamTrack;
  if (!ms.getTracks().includes(mt)) ms.addTrack(mt);
  vid.srcObject = ms;
  vid.playsInline = true;
  vid.style.display = 'block';
  playRemote(vid);
}

function detachRemote(track: RemoteTrack | null, p: RemoteParticipant): void {
  const seat = seatOf(p);
  const vid = seat == null ? null : vidFor(seat);
  if (!vid) return;
  if (!track) { vid.srcObject = null; vid.style.display = 'none'; return; }
  const ms = vid.srcObject instanceof MediaStream ? vid.srcObject : null;
  if (ms) ms.removeTrack(track.mediaStreamTrack);
  if (!ms || ms.getTracks().length === 0) { vid.srcObject = null; vid.style.display = 'none'; }
}

/** Publish the local camera and mic, and unpublish anything that was turned off. */
async function publishLocal(): Promise<void> {
  const r = room;
  if (!r || !myStream) return;
  const lp = r.localParticipant;
  const live = myStream.getTracks().filter((t) => t.readyState === 'live');
  for (const pub of [...lp.trackPublications.values()]) {
    const mt = pub.track?.mediaStreamTrack;
    if (mt && !live.includes(mt)) { try { await lp.unpublishTrack(mt, true); } catch (e) { /* ignore */ } }
  }
  for (const t of live) {
    const done = [...lp.trackPublications.values()].some((p) => p.track?.mediaStreamTrack === t);
    if (!done) {
      try {
        await lp.publishTrack(t, { source: t.kind === 'video' ? Track.Source.Camera : Track.Source.Microphone });
        console.log('[media] published', t.kind);
      } catch (e) { console.warn('[media] publish failed', t.kind, e); }
    }
  }
}

/** Called by the camera code when the local camera or mic changes. */
export function mediaSyncLocal(): void {
  if (room) void publishLocal();
}

function fallBack(peers: any[]): void {
  room = null;
  starting = false;
  if (meshFallback) meshFallback(peers || []);
}

/** Join the LiveKit room for this table seat. Safe to call often; it does nothing if already joined. */
export function startMedia(): void {
  if (offMode || starting || room || !NET.active || !NET.code || NET.mySeat == null) return;
  starting = true;
  bindUnlock();
  socket.emit('mediaToken', async (res: any) => {
    console.log('[media] token reply', res && (res.error || 'ok'));
    const peers = (res && res.peers) || [];
    if (!res || res.error) {
      if (res && res.error === 'off') { offMode = true; starting = false; fallBack(peers); return; }
      // transient (e.g. seat not ready): use the mesh for now and let the next event retry
      fallBack(peers);
      return;
    }
    const r = new Room({ adaptiveStream: true, dynacast: true });
    room = r;
    r.on(RoomEvent.TrackSubscribed, (track, _pub, p) => { console.log('[media] subscribed', track.kind, p.identity); attachRemote(track as RemoteTrack, p); });
    r.on(RoomEvent.TrackUnsubscribed, (track, _pub, p) => detachRemote(track as RemoteTrack, p));
    r.on(RoomEvent.ParticipantDisconnected, (p) => detachRemote(null, p));
    r.on(RoomEvent.Disconnected, () => { if (room === r) { room = null; starting = false; } });
    try {
      await r.connect(res.url, res.token);
      console.log('[media] connected to', res.room, 'as', r.localParticipant.identity);
      starting = false;
      await r.startAudio().catch(() => { /* waits for a tap */ });
      await publishLocal();
    } catch (e) {
      try { r.disconnect(); } catch (err) { /* ignore */ }
      fallBack(peers);
    }
  });
}

/** Leave the LiveKit room (when leaving the table). */
export function stopMedia(): void {
  const r = room;
  room = null;
  starting = false;
  if (r) { try { r.disconnect(); } catch (e) { /* ignore */ } }
}
